<script lang="ts">
  import { animations } from '@/utils/animations';
  import Icon from '@ui/base/Icon.svelte';
  import { gameEventBus } from '@/game/events';
  import { Upgrades } from '@game/database';
  import { Crafts } from '@game/database/CraftDB';
  import { profileStore } from '@stores/game/profile';
  import { onMount, onDestroy } from 'svelte';

  // Подписываемся на изменения профиля для реактивности
  const profile = $derived($profileStore);

  // Создаем атомы для принудительного обновления
  let upgradeTrigger = $state(0);
  let craftTrigger = $state(0);
  let warehouseTrigger = $state(0);

  // Реактивно проверяем доступные апгрейды
  const hasUpgrades = $derived.by(() => {
    // Подписываемся на изменения профиля и триггеров
    profile;
    upgradeTrigger;

    const availableUpgrades = Upgrades.getAvailableUpgrades();
    const result = availableUpgrades.some(upgrade => upgrade.canAfford);
    console.log(
      '🔔 BottomActionBar: hasUpgrades recalculated:',
      result,
      'upgrades count:',
      availableUpgrades.length
    );
    return result;
  });

  // Реактивно проверяем активные крафты (подписка на стор)
  const activeCraftsReadable = Crafts.activeCraftsStore();
  const activeCrafts = $derived($activeCraftsReadable);
  const hasActiveCrafts = $derived(activeCrafts.length > 0);

  // Реактивно проверяем наличие товаров на складе
  const hasWarehouseItems = $derived.by(() => {
    // Подписываемся на изменения профиля и триггеров
    profile;
    warehouseTrigger;

    const inventoryItems = Object.values(profile?.inventory?.items || {});
    const sellableItems = inventoryItems.filter(item => item.type === 'ore' && item.quantity > 0);
    const result = sellableItems.length > 0;
    console.log(
      '📦 BottomActionBar: hasWarehouseItems recalculated:',
      result,
      'sellable items count:',
      sellableItems.length
    );
    return result;
  });

  let unsubscribe: (() => void)[] = [];

  onMount(() => {
    // Подписываемся на изменения уровней апгрейдов
    unsubscribe.push(
      Upgrades.subscribeToUpgradeLevels(() => {
        console.log('🔄 BottomActionBar: Upgrade levels changed, triggering update');
        upgradeTrigger++;
      })
    );

    // Подписываемся на изменения активных крафтов
    unsubscribe.push(
      Crafts.subscribeToActiveCrafts(() => {
        console.log('🔄 BottomActionBar: Active crafts changed, triggering update');
        craftTrigger++;
      })
    );

    // Подписываемся на изменения профиля для склада
    unsubscribe.push(
      profileStore.subscribe(() => {
        console.log('🔄 BottomActionBar: Profile changed, triggering warehouse update');
        warehouseTrigger++;
      })
    );
  });

  onDestroy(() => {
    unsubscribe.forEach(fn => fn());
  });

  // Game modes that will be unlocked progressively
  const gameModes = $derived([
    {
      id: 'warehouse',
      icon: 'warehouse',
      label: 'Warehouse',
      unlocked: true,
      color: 'neon-green' as const,
      hasNotifications: hasWarehouseItems,
    },
    {
      id: 'upgrades',
      icon: 'upgrades',
      label: 'Upgrades',
      unlocked: true,
      color: 'neon-blue' as const,
      hasNotifications: hasUpgrades,
    },
    {
      id: 'production',
      icon: 'production',
      label: 'Production',
      unlocked: true,
      color: 'neon-orange' as const,
      hasNotifications: hasActiveCrafts,
    },
    {
      id: 'research',
      icon: 'research',
      label: 'Research',
      unlocked: false,
      color: 'neon-purple' as const,
    },
    {
      id: 'market',
      icon: 'market',
      label: 'Market',
      unlocked: false,
      color: 'neon-pink' as const,
    },
  ]);

  function handleModeClick(modeId: string) {
    // Отправляем событие через GameEventBus
    gameEventBus.emit('mode-click', { modeId });
  }

  function animateButton(event: MouseEvent) {
    const button = event.currentTarget as HTMLElement;
    if (button) {
      animations.buttonPress(button);
    }
  }
</script>

<div class="bottom-action-bar">
  <div class="action-buttons">
    {#each gameModes as mode (mode.id)}
      <button
        class="action-button"
        class:unlocked={mode.unlocked}
        class:locked={!mode.unlocked}
        disabled={!mode.unlocked}
        onclick={e => {
          if (mode.unlocked) {
            animateButton(e);
            handleModeClick(mode.id);
          }
        }}
        aria-label={mode.label}
      >
        <div class="action-icon">
          <Icon
            name={mode.icon}
            size="lg"
            color={mode.unlocked ? mode.color : 'gray'}
            variant="action"
            hover="glow"
          />
        </div>
        {#if !mode.unlocked}
          <div class="lock-overlay">
            <Icon name="lock" size="sm" color="gray" />
          </div>
        {/if}

        {#if mode.hasNotifications}
          <div class="notification-badge">
            <div class="notification-dot"></div>
          </div>
        {/if}
      </button>
    {/each}
  </div>
</div>

<style lang="postcss">
  @reference "@/styles/theme.css";

  .bottom-action-bar {
    @apply fixed bottom-0 left-0 right-0 z-50;
    @apply bg-dark-darker/95 backdrop-blur-sm;
    @apply border-t border-neon-green/30;
    @apply pointer-events-auto;
  }

  .action-buttons {
    @apply flex;
    @apply h-16;
    @apply overflow-hidden;
  }

  .action-button {
    @apply relative flex-1;
    @apply flex items-center justify-center;
    @apply transition-all duration-200;
    @apply cursor-pointer;
    @apply active:scale-95;
    @apply border-r border-neon-green/20;
  }

  .action-button:last-child {
    @apply border-r-0;
  }

  .action-button.unlocked {
    @apply bg-dark-bg/50;
    @apply hover:bg-neon-green/10;
    @apply hover:shadow-inner hover:shadow-neon-green/20;
  }

  .action-button.locked {
    @apply bg-gray-800/50;
    @apply cursor-not-allowed;
  }

  .action-icon {
    @apply flex items-center justify-center;
  }

  .lock-overlay {
    @apply absolute inset-0;
    @apply flex items-center justify-center;
    @apply bg-black/60;
    @apply text-sm;
  }

  .notification-badge {
    @apply absolute top-1 right-1;
    @apply pointer-events-none;
  }

  .notification-dot {
    @apply w-2 h-2;
    @apply bg-neon-green rounded-full;
    @apply animate-pulse;
    @apply shadow-lg shadow-neon-green/50;
  }
</style>
